import React, { useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/auth.provider';
import authService from '../../services/api/auth.service';
import validator from 'validator';
import toast from 'react-hot-toast';

interface FormData {
    first_name: string;
    last_name: string;
    email: string;
    pan: string;
}

interface FormErrors {
    first_name: string;
    last_name: string;
    email: string;
    pan: string;
}

const RegistrationForm = ({ authToken }: { authToken: string }) => {
    const [formData, setFormData] = useState<FormData>({
        first_name: '',
        last_name: '',
        email: '',
        pan: '',
    });

    const [errors, setErrors] = useState<FormErrors>({
        first_name: '',
        last_name: '',
        email: '',
        pan: '',
    });

    const { login } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [jwtToken, setJwtToken] = useState<string>('');

    const validateField = (name: string, value: string): string => {
        switch (name) {
            case 'first_name':
                return validator.isAlpha(value) && validator.isLength(value, { min: 2, max: 50 })
                    ? ''
                    : 'First name must be between 2 and 50 characters long and contain only letters';
            case 'last_name':
                return validator.isAlpha(value) && validator.isLength(value, { min: 2, max: 50 })
                    ? ''
                    : 'Last name must be between 2 and 50 characters long and contain only letters';
            case 'email':
                return validator.isEmail(value) ? '' : 'Please enter a valid email address';
            case 'pan':
                const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                return panRegex.test(value)
                    ? ''
                    : 'Please enter a valid PAN number. Example: ABCDE1234A';
            default:
                return '';
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const validateForm = (): boolean => {
        const newErrors = {
            first_name: validateField('first_name', formData.first_name),
            last_name: validateField('last_name', formData.last_name),
            email: validateField('email', formData.email),
            pan: validateField('pan', formData.pan),
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const response = await authService.addDetails(formData, authToken);
            login(response.data.token);
            setShowModal(true);
            toast.success(response.message);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        }
    };

    const navigate = useNavigate();

    const handleGetStarted = () => {
        setShowModal(false);
        login(jwtToken);
        localStorage.setItem('welcome', 'true');
        navigate('/');
    };

    return (
        <div className="max-w-md mx-auto py-4 sm:py-6">
            <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="space-y-2">
                    <label className="block text-sm text-gray-600">
                        First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="first_name"
                        required
                        className={`w-full p-3 border ${errors.first_name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-700 focus:border-primary-700 outline-none`}
                        placeholder="First Name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                    />
                    {errors.first_name && (
                        <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="block text-sm text-gray-600">
                        Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="last_name"
                        required
                        className={`w-full p-3 border ${errors.last_name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-700 focus:border-primary-700 outline-none`}
                        placeholder="Last Name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                    />
                    {errors.last_name && (
                        <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="block text-sm text-gray-600">
                        PAN Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="pan"
                        required
                        pattern="[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}"
                        title="PAN should be in XXXXX1234X format"
                        className={`w-full p-3 border ${errors.pan ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-700 focus:border-primary-700 outline-none`}
                        placeholder="Enter PAN number"
                        value={formData.pan}
                        onChange={handleInputChange}
                    />
                    {errors.pan && (
                        <p className="text-red-500 text-sm mt-1">{errors.pan}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="block text-sm text-gray-600">
                        Email address <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        name="email"
                        required
                        className={`w-full p-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-700 focus:border-primary-700 outline-none`}
                        placeholder="Enter email"
                        value={formData.email}
                        onChange={handleInputChange}
                    />
                    {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                </div>

                <button
                    type="submit"
                    className="w-full mt-6 py-3 px-4 bg-primary-800 text-white rounded-lg hover:bg-primary-900 transition-colors duration-200"
                >
                    Continue
                </button>
            </form>

            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-4 sm:p-0">
                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
                    <div className="relative bg-white rounded-xl p-4 sm:p-6 md:p-8 max-w-md w-full mx-auto z-50 overflow-y-auto max-h-[90vh]">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <FaCheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                            </div>

                            <h2 className="text-xl sm:text-2xl font-semibold mb-2">Congratulations!</h2>
                            <p className="text-gray-600 text-sm sm:text-base mb-4">
                                You've successfully registered 🎉
                            </p>

                            <button
                                onClick={handleGetStarted}
                                className="block w-full mt-6 py-3 px-4 bg-primary-800 text-white text-center rounded-lg hover:bg-primary-900 transition-colors duration-200 text-sm sm:text-base"
                            >
                                Get started
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegistrationForm;